// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../utils/api.js';
import Logo from '../components/shared/Logo.jsx';
import { CheckCircle } from 'lucide-react';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [phase,       setPhase]       = useState('loading'); // loading | form | success | error
  const [tokens,      setTokens]      = useState({ access_token: '', refresh_token: '' });
  const [password,    setPassword]    = useState('');
  const [confirm,     setConfirm]     = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [err,         setErr]         = useState('');

  useEffect(() => {
    // Supabase sends tokens in the URL hash: #access_token=...&refresh_token=...&type=recovery
    const hash = window.location.hash.slice(1);
    if (!hash) {
      setPhase('error');
      return;
    }
    const params = new URLSearchParams(hash);
    const access_token  = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    const type          = params.get('type');

    if (!access_token || type !== 'recovery') {
      setPhase('error');
      return;
    }

    setTokens({ access_token, refresh_token: refresh_token || '' });
    setPhase('form');
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (password.length < 8) return setErr('Password must be at least 8 characters');
    if (password !== confirm)  return setErr('Passwords do not match');

    setSubmitting(true);
    try {
      await api.changePassword(tokens.access_token, tokens.refresh_token, password);
      setPhase('success');
    } catch (e) {
      setErr(e.message || 'Failed to update password. The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 32 }}>
          <Logo size={40} iconSize={28} />
        </div>

        {phase === 'loading' && (
          <p style={{ color: 'var(--color-text-2)', fontSize: 14 }}>Loading…</p>
        )}

        {phase === 'error' && (
          <>
            <h1 style={titleStyle}>Invalid reset link</h1>
            <p style={{ color: 'var(--color-text-2)', fontSize: 14, marginBottom: 24 }}>
              This password reset link is invalid or has expired. Please request a new one from the Settings page.
            </p>
            <Link to="/login" style={backLink}>Back to sign in</Link>
          </>
        )}

        {phase === 'form' && (
          <>
            <h1 style={titleStyle}>Set new password</h1>
            <p style={{ color: 'var(--color-text-2)', fontSize: 14, marginBottom: 24 }}>
              Choose a new password for your account.
            </p>
            {err && <div style={errBanner}>{err}</div>}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field
                label="New password"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="Min. 8 characters"
                autoComplete="new-password"
              />
              <Field
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={setConfirm}
                placeholder="Repeat your password"
                autoComplete="new-password"
              />
              <button type="submit" style={submitBtn(submitting)} disabled={submitting}>
                {submitting ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </>
        )}

        {phase === 'success' && (
          <>
            <div style={{ marginBottom: 16 }}><CheckCircle size={40} style={{ color: 'var(--color-green)' }} /></div>
            <h1 style={titleStyle}>Password updated</h1>
            <p style={{ color: 'var(--color-text-2)', fontSize: 14, marginBottom: 24 }}>
              Your password has been changed. You can now sign in with your new password.
            </p>
            <button onClick={() => navigate('/login')} style={submitBtn(false)}>
              Sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, type, value, onChange, placeholder, autoComplete }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text)' }}>{label}</label>
      <input
        type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        style={{
          padding: '10px 14px', border: '1px solid var(--color-border)',
          borderRadius: 8, fontSize: 14, color: 'var(--color-text)',
          background: 'var(--color-surface)', outline: 'none',
          transition: 'border-color 0.15s',
        }}
        onFocus={e => e.target.style.borderColor = 'var(--color-primary)'}
        onBlur={e => e.target.style.borderColor = 'var(--color-border)'}
      />
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-bg)', padding: 24,
};
const card = {
  background: 'var(--color-surface)', borderRadius: 14, padding: '40px 36px',
  width: '100%', maxWidth: 400, boxShadow: 'var(--shadow-card)',
  border: '1px solid var(--color-border)', textAlign: 'center',
};
const titleStyle = { fontSize: 22, fontWeight: 700, marginBottom: 10 };
const errBanner = {
  background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
  color: 'var(--color-red-text)', borderRadius: 8, padding: '11px 14px',
  marginBottom: 4, fontSize: 14, textAlign: 'left',
};
const submitBtn = (loading) => ({
  background: loading ? 'var(--color-text-3)' : 'var(--color-primary)',
  color: '#fff', border: 'none', borderRadius: 8, padding: 12,
  fontSize: 15, fontWeight: 600, cursor: loading ? 'default' : 'pointer',
  marginTop: 4, width: '100%',
});
const backLink = { color: 'var(--color-primary)', fontWeight: 600, fontSize: 14 };
